import * as THREE from 'three';

/**
 * VR 里的 3D 文字键盘（CanvasTexture）。
 *
 * DOM Overlay 在 Quest 浏览器上实测不可用（`session.domOverlayState` 为空），
 * 系统虚拟键盘就弹不出来，所以只能自己做。射线命中 UV → 画布坐标 → 判断按键。
 *
 * 布局：QWERTY 三行 + 空格 / 退格 / 发送 / 关闭。
 */
const CANVAS_W = 660;
const CANVAS_H = 440;
const KEY_H = 56;
const KEY_GAP = 5;

const ROWS = ['1234567890', 'qwertyuiop', 'asdfghjkl', 'zxcvbnm'];

interface Key {
  rect: [number, number, number, number];
  label: string;
  action: 'char' | 'space' | 'back' | 'send' | 'close';
  char?: string;
}

export class VrKeyboard {
  readonly group = new THREE.Group();
  readonly interactables: THREE.Object3D[] = [];

  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly texture: THREE.CanvasTexture;
  private readonly mesh: THREE.Mesh;
  private readonly keys: Key[] = [];

  private text = '';
  private hovered = -1;

  constructor(private readonly onSend: (text: string) => void) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = CANVAS_W;
    this.canvas.height = CANVAS_H;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('2d context unavailable');
    this.ctx = ctx;

    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;

    this.mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(0.8, 0.533),
      new THREE.MeshBasicMaterial({ map: this.texture, transparent: true }),
    );
    this.mesh.name = 'vr-keyboard';
    this.group.add(this.mesh);
    this.interactables.push(this.mesh);
    this.group.position.set(-0.42, 1.15, -1.1);
    this.group.visible = false;

    this.buildKeys();
    this.redraw();
  }

  private buildKeys(): void {
    let y = 92;
    for (const row of ROWS) {
      const width = row.length * (KEY_H - KEY_GAP) + (row.length - 1) * KEY_GAP;
      let x = (CANVAS_W - width) / 2;
      for (const ch of row) {
        this.keys.push({ rect: [x, y, KEY_H - KEY_GAP, KEY_H - KEY_GAP], label: ch, action: 'char', char: ch });
        x += KEY_H;
      }
      y += KEY_H;
    }

    // 功能键
    const funcY = y + 6;
    this.keys.push({ rect: [24, funcY, 150, 48], label: '空格', action: 'space' });
    this.keys.push({ rect: [186, funcY, 110, 48], label: '退格', action: 'back' });
    this.keys.push({ rect: [308, funcY, 84, 48], label: '发送', action: 'send' });
    this.keys.push({ rect: [404, funcY, 84, 48], label: '关闭', action: 'close' });
  }

  private redraw(): void {
    const c = this.ctx;
    c.clearRect(0, 0, CANVAS_W, CANVAS_H);

    // 背板
    c.fillStyle = 'rgba(12, 20, 28, 0.94)';
    c.strokeStyle = 'rgba(124, 199, 224, 0.6)';
    c.lineWidth = 3;
    c.beginPath();
    c.rect(4, 4, CANVAS_W - 8, CANVAS_H - 8);
    c.fill();
    c.stroke();

    // 输入框
    c.fillStyle = 'rgba(255, 255, 255, 0.1)';
    c.strokeStyle = 'rgba(124, 199, 224, 0.45)';
    c.lineWidth = 2;
    c.beginPath();
    c.rect(20, 20, CANVAS_W - 40, 56);
    c.fill();
    c.stroke();

    c.fillStyle = this.text ? '#eaf4fa' : 'rgba(180, 200, 214, 0.55)';
    c.font = '30px "PingFang SC", "Microsoft YaHei", system-ui, sans-serif';
    c.textAlign = 'left';
    c.textBaseline = 'middle';
    const shown = this.text || '点击字母输入…';
    c.fillText(shown.slice(-22), 34, 50);

    // 按键
    this.keys.forEach((key, index) => {
      const [x, y, w, h] = key.rect;
      const hover = index === this.hovered;
      c.fillStyle = hover ? 'rgba(63, 127, 160, 0.95)' : 'rgba(30, 46, 60, 0.95)';
      c.strokeStyle = hover ? 'rgba(180, 230, 250, 0.9)' : 'rgba(90, 130, 150, 0.55)';
      c.lineWidth = 2;
      c.beginPath();
      c.rect(x, y, w, h);
      c.fill();
      c.stroke();

      c.fillStyle = '#eaf4fa';
      c.font = '28px "PingFang SC", "Microsoft YaHei", system-ui, sans-serif';
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      c.fillText(key.label, x + w / 2, y + h / 2 + 1);
    });

    this.texture.needsUpdate = true;
  }

  /** 射线命中：UV → 画布坐标 → 按键 */
  handleHit(uv: { x: number; y: number } | null, pressed: boolean): void {
    if (!uv) {
      if (this.hovered !== -1) {
        this.hovered = -1;
        this.redraw();
      }
      return;
    }

    const px = uv.x * CANVAS_W;
    const py = (1 - uv.y) * CANVAS_H;
    const index = this.keys.findIndex(({ rect }) => {
      const [x, y, w, h] = rect;
      return px >= x && px <= x + w && py >= y && py <= y + h;
    });

    if (index !== this.hovered) {
      this.hovered = index;
      this.redraw();
    }
    if (!pressed || index < 0) return;

    const key = this.keys[index];
    if (!key) return;
    switch (key.action) {
      case 'char':
        if (this.text.length < 60) this.text += key.char ?? '';
        break;
      case 'space':
        if (this.text.length < 60) this.text += ' ';
        break;
      case 'back':
        this.text = this.text.slice(0, -1);
        break;
      case 'send':
        if (this.text.trim()) this.onSend(this.text.trim());
        this.text = '';
        this.setVisible(false);
        break;
      case 'close':
        this.text = '';
        this.setVisible(false);
        break;
    }
    this.redraw();
  }

  setVisible(visible: boolean): void {
    this.group.visible = visible;
    if (visible) this.redraw();
  }

  get visible(): boolean {
    return this.group.visible;
  }
}
